import React, {useState} from "react";
import {rentalTypes} from "../../variables/rentalTypes";
import {amenityTypes} from "../../variables/amenityTypes";
import {
    Badge,
    Box,
    Button,
    Card,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel, Heading,
    Icon,
    Image,
    Input, InputGroup,
    InputLeftAddon, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    SimpleGrid, Switch,
    Text,
    useColorModeValue, useDisclosure, useToast
} from "@chakra-ui/react";
import {IoBed, IoPeople} from "react-icons/io5";
import {postData, putData} from "../../services/api";
import {Controller, useForm} from "react-hook-form";
import {customScrollbar} from "../scrollbar/CustomScrollbar";
import {FaBath} from "react-icons/fa";
import MapboxMoveable from "../maps/MapboxMoveable";
import CharacterCounter from "../counter/CharacterCounter";
import TruncatedText from "../text/TruncatedText";
import FileDropAndSort from "../../components/dragndrop/FileDropAndSort";
import Mapbox from "../maps/Mapbox";
import MyCKEditor from "../editor/MyCKEditor";
import PlaceSearch from "../googleplaces/PlaceSearch";
import RatingStars from "../googleplaces/RatingStars";

export default function RentalDetails(props) {

    const rental = props.data;

    const {isOpen, onOpen, onClose} = useDisclosure()
    const {isOpen: isOpenRating, onOpen: onOpenRating, onClose: onCloseRating} = useDisclosure()

    const brandStars = useColorModeValue("brand.500", "brand.400");

    const [shownField, setShownField] = useState('');
    const [selectedTypes, setSelectedTypes] = useState(rental.types);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedAmenities, setSelectedAmenities] = useState(rental.amenities);
    const [selectedPictures, setSelectedPictures] = useState(rental.pictures);
    const [showGoogleReviews, setShowGoogleReviews] = useState(!!rental.google_place_id);
    const [address, setAddress] = useState(null);
    const [coordinates, setCoordinates] = useState({lat: rental.lat, lng: rental.lng});

    const toast = useToast()

    const {
        handleSubmit,
        register,
        formState: {errors, isSubmitting},
        watch,
        control,
        setValue,
        getValues
    } = useForm({
        defaultValues: rental
    })

    const handleShowField = (type) => {
        setShownField(type);

        const element = document.getElementById('edit');
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start', // Scroll to the top of the element
            });
        }
    };

    const isActiveField = (fieldType) => {
        return shownField === fieldType ? 'black.200' : 'gray.200';
    };

    const isValidDescription = watch('description')?.replace(/<[^>]*>/g, '').length >= 1 && watch('description')?.replace(/<[^>]*>/g, '').length <= 3000;

    function onSubmit(values) {
        return new Promise(async (resolve) => {
            try {
                values.pictures = selectedPictures;
                values.types = selectedTypes;
                values.amenities = selectedAmenities;
                values.lat = coordinates.lat;
                values.lng = coordinates.lng;

                await putData('/api/rentals/' + rental?.id, values);
                resolve();

                toast({
                    title: 'Inserat wurde aktualisiert',
                    status: 'success',
                    isClosable: true,
                })
            } catch (error) {
                toast({
                    title: 'Inserat konnte nicht aktualisiert werden: ' + error.message,
                    status: 'error',
                    isClosable: true,
                })

                resolve();
            }
        })
    }

    const handleSelectType = (typeId) => {
        setSelectedTypes(prevSelected => {
            if (prevSelected.includes(typeId)) {
                return prevSelected.filter(id => id !== typeId);
            } else {
                return [...prevSelected, typeId];
            }
        });
    };

    const handleSelectAmenity = (amenityId) => {
        setSelectedAmenities(prevSelected => {
            if (prevSelected.includes(amenityId)) {
                return prevSelected.filter(id => id !== amenityId);
            } else {
                return [...prevSelected, amenityId];
            }
        });
    };

    const handleFilesSelected = (files) => {
        setSelectedPictures(files);
    };

    const handleUrlChange = (fieldName) => (e) => {
        let value = e.target.value;

        if (value.trim() === "" || value.trim() === "https://") {
            setValue(fieldName, "", {shouldValidate: true});
            return true;
        }

        try {
            const modifiedValue = !/^https?:\/\//i.test(value) ? `https://${value}` : value;
            setValue(fieldName, modifiedValue, {shouldValidate: true});
            new URL(modifiedValue);
            return true;
        } catch (error) {
            return "Please enter a valid URL";
        }
    };

    const handleCoordinatesChange = (coordinates) => {
        setCoordinates(coordinates)
    };

    const handleAddressChange = (address) => {
        setValue('temp_street', address?.street);
        setValue('temp_zip', address?.postcode);
        setValue('temp_place', address?.place);
        setValue('temp_country', address?.country);
        setAddress(address)
    };

    const handleUpdateAddress = () => {
        setValue('street', watch('temp_street'));
        setValue('zip', watch('temp_zip'));
        setValue('place', watch('temp_place'));
        setValue('country', watch('temp_country'));
        onClose()
    };

    const handleOptimize = async () => {
        const description = getValues('description');

        setIsLoading(true);
        try {
            const {optimized_text} = await postData('/api/ai/optimize', {
                text: description,
            });
            setValue('description', optimized_text);
        } catch (error) {
            toast({
                title: 'Es ist ein Fehler aufgetreten: ' + error.message,
                status: 'error',
                isClosable: true,
            })
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const handleGooglePlaceSelected = (place) => {
        if(place){
            setValue('google_place_id', place.place_id)
            setValue('google_place_data', place)
            onCloseRating()
        }
    }
    const toggleShowGoogleReviews = () => {
        setShowGoogleReviews(!showGoogleReviews);
        setValue('google_place_id', null);
        setValue('google_place_data', null);
    }

    const formatAddress = () => {
        const street = watch('street');
        const zip = watch('zip');
        const place = watch('place');
        const country = watch('country');

        return [street, `${zip} ${place}`, country].filter(Boolean).join(', ');
    };
    const formattedAddress = formatAddress();

    return (
        <Box>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Flex gap={5} direction={{base: "column", lg: 'row'}}>
                    <Box w={{base: '100%', lg: '50%'}} pr={5} color='grey' borderRight={"1px"}
                         height={{base: 'auto', lg: '75vh'}} overflowY={{base: 'auto', lg: 'scroll'}}
                         sx={customScrollbar}>
                        <Card sx={{cursor: 'pointer'}} boxShadow='lg' p={"5"} mb={"2"}
                              onClick={() => handleShowField('types')} border='1px'
                              borderColor={isActiveField('types')}>
                            <Text fontSize='md' fontWeight={700} mb='2'>Art der Unterkunft</Text>
                            <Text color='grey'
                                  fontSize='xs'>{rentalTypes.filter(type => selectedTypes?.includes(type.id)).map(type => type.name).join(', ')}</Text>
                        </Card>
                        <Card sx={{cursor: 'pointer'}} boxShadow='lg' p={"5"} mb={"2"}
                              onClick={() => handleShowField('title')} border='1px'
                              borderColor={isActiveField('title')}>
                            <Text fontSize='md' fontWeight={700} mb='2'>Titel und Beschreibung</Text>
                            <Text color='grey' fontSize='xl'>{watch("title")}</Text>
                            <TruncatedText color='grey' content={watch("description")} maxLength={100}/>
                        </Card>
                        <Card sx={{cursor: 'pointer'}} boxShadow='lg' p={"5"} mb={"2"}
                              onClick={() => handleShowField('pictures')} border='1px'
                              borderColor={isActiveField('pictures')}>
                            <Text fontSize='md' fontWeight={700} mb='2'>Bilder</Text>
                            <Flex justifyItems={'center'}>
                                {selectedPictures[1] ?
                                    <Image display={{base: 'none', lg: 'block'}} zIndex={'1'}
                                           transform="translateX(50px) scale(0.9) rotate(-5deg)"
                                           width={"150px"} height={"150px"} objectFit={'cover'} borderRadius={10}
                                           src={selectedPictures[1].url ?? selectedPictures[1].base64 ?? ''}
                                           alt={selectedPictures[1].name}/> : ''}
                                {selectedPictures[0] ?
                                    <Box zIndex={'2'} transform="scale(1.1)" width={"150px"} height={"150px"}>
                                        <Badge position={'absolute'} top={2}
                                               left={2}>{selectedPictures ? selectedPictures.length : 0} Fotos</Badge>
                                        <Image width={"100%"} height={"100%"}
                                               objectFit={'cover'} borderRadius={10}
                                               src={selectedPictures[0].url ?? selectedPictures[0].base64 ?? ''}
                                               alt={selectedPictures[0].name}/>
                                    </Box>
                                    : ''}
                                {selectedPictures[2] ?
                                    <Image display={{base: 'none', lg: 'block'}} zIndex={'1'}
                                           transform="translateX(-50px) scale(0.9) rotate(5deg)"
                                           width={"150px"} height={"150px"} objectFit={'cover'} borderRadius={10}
                                           src={selectedPictures[2].url ?? selectedPictures[2].base64 ?? ''}
                                           alt={selectedPictures[2].name}/> : ''}
                            </Flex>
                        </Card>
                        <Card sx={{cursor: 'pointer'}} boxShadow='lg' p={"5"} mb={"2"}
                              onClick={() => handleShowField('amenities')} border='1px'
                              borderColor={isActiveField('amenities')}>
                            <Text fontSize='md' fontWeight={700} mb='2'>Ausstattungsmerkmale</Text>
                            <Text color='grey'
                                  fontSize='xs'>{amenityTypes.filter(type => selectedAmenities?.includes(type.id)).map(type => type.name).join(', ')}</Text>
                        </Card>
                        <Card sx={{cursor: 'pointer'}} boxShadow='lg' p={"5"} mb={"2"}
                              onClick={() => handleShowField('general')} border='1px'
                              borderColor={isActiveField('general')}>
                            <Text fontSize='md' fontWeight={700} mb='2'>Allgemeine Angaben</Text>
                            <Text color='grey' fontSize='xs'>{watch("price")} € pro Nacht</Text>
                            <Text color='grey' fontSize='xs'>{watch("guests")} Gäste</Text>
                            <Text color='grey' fontSize='xs'>{watch("bedrooms")} Schlafzimmer</Text>
                            <Text color='grey' fontSize='xs'>{watch("bathrooms")} Badezimmer</Text>
                            <Text color='grey' fontSize='xs'>{watch("size")}qm</Text>
                        </Card>
                        <Card sx={{cursor: 'pointer'}} boxShadow='lg' p={"5"} mb={"2"}
                              onClick={() => handleShowField('address')} border='1px'
                              borderColor={isActiveField('address')}>
                            <Text fontSize='md' fontWeight={700} mb='2'>Adresse</Text>
                            <Text color='grey' fontSize='xs'>{watch("street")}, {watch("zip")} {watch("place")}</Text>
                        </Card>
                        <Card sx={{cursor: 'pointer'}} boxShadow='lg' p={"5"} mb={"2"}
                              onClick={() => handleShowField('additional_information')} border='1px'
                              borderColor={isActiveField('additional_information')}>
                            <Text fontSize='md' fontWeight={700} mb='2'>Wichtige Informationen</Text>
                            {watch("additional_information") ?
                                <TruncatedText color='grey' content={watch("additional_information")}
                                               maxLength={100}/> :
                                <Text color='grey' fontSize='xs'>Keine Angaben</Text>}
                        </Card>
                        <Card sx={{cursor: 'pointer'}} boxShadow='lg' p={"5"} mb={"2"}
                              onClick={() => handleShowField('ratings')} border='1px'
                              borderColor={isActiveField('ratings')}>
                            <Text fontSize='md' fontWeight={700} mb='2'>Bewertungen</Text>
                            <Text color='grey'
                                  fontSize='xs'>Google: {showGoogleReviews ? 'Aktiviert' :
                                'Nicht aktiviert'}</Text>
                        </Card>
                        <Card sx={{cursor: 'pointer'}} boxShadow='lg' p={"5"} mb={"2"}
                              onClick={() => handleShowField('optional')} border='1px'
                              borderColor={isActiveField('optional')}>
                            <Text fontSize='md' fontWeight={700} mb='2'>Optionale Angaben</Text>
                            <Text color='grey' fontSize='xs'>Webseite: {watch("link") ?? 'Keine Angabe'}</Text>
                            <Text color='grey' fontSize='xs'>Facebook: {watch("facebook") ?? 'Keine Angabe'}</Text>
                            <Text color='grey' fontSize='xs'>Instagram: {watch("instagram") ?? 'Keine Angabe'}</Text>
                        </Card>
                    </Box>
                    <Box id={"edit"} w={{base: '100%', lg: '50%'}} pr={{base: 0, lg: 5}} color='grey'
                         height={{base: 'auto', lg: '75vh'}} overflowY={{base: 'auto', lg: 'scroll'}}
                         sx={customScrollbar}>
                        {shownField === 'pictures' && <Card boxShadow='lg' p={"5"} mb={"2"}>
                            <FileDropAndSort rentalId={rental.id} files={selectedPictures}
                                             setFiles={handleFilesSelected}/>
                        </Card>}

                        {shownField === 'title' && <Card boxShadow='lg' p={"5"} mb={"2"}>
                            <Heading mb={5}>Titel und Beschreibung</Heading>
                            <Text fontSize={'md'} color='grey' mb={5}>Du hast folgenden Titel für dein Inserat
                                gewählt</Text>
                            <FormControl mb='24px' isInvalid={errors.title}>
                                <Input
                                    id='title'
                                    fontSize='sm'
                                    type='text'
                                    fontWeight='500'
                                    size='lg'
                                    {...register('title', {
                                        required: 'Bitte geben Sie einen Titel an',
                                        minLength: {value: 1, message: 'Titel muss mindestens 1 Zeichen lang sein'},
                                        maxLength: {value: 60, message: 'Titel darf maximal 60 Zeichen lang sein'},
                                    })}
                                />
                                <CharacterCounter maxLength={60} text={watch('title')}/>
                                <FormErrorMessage>
                                    {errors.title && errors.title.message}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl className={watch('description') && !isValidDescription ? "form-error" : ""}>
                                <Text fontSize={'md'} color='grey' mb={5}>Diese Beschreibung hast du für dein Inserat
                                    hinterlegt</Text>
                                <Controller
                                    name="description"
                                    control={control}
                                    defaultValue=""
                                    render={({field}) => (<MyCKEditor field={field}/>)}
                                />
                                <CharacterCounter maxLength={3000} text={watch('description')}/>
                                <Button
                                    mt={3}
                                    onClick={handleOptimize}
                                    isLoading={isLoading}
                                    loadingText="Optimieren..."
                                    colorScheme="teal"
                                    leftIcon={<span role="img" aria-label="magic">✨</span>} // Zauberstab-Icon
                                >
                                    Text von KI optimieren lassen
                                </Button>
                            </FormControl>
                        </Card>}

                        {shownField === 'types' && <Card boxShadow='lg' p={"5"} mb={"2"}>
                            <Heading mb={5}>Unterkunftsart</Heading>
                            <Text fontSize={'md'} color='grey' mb={5}>Diese Arten hast du für deine Unterkunft
                                ausgewählt</Text>
                            <SimpleGrid columns={{base: 1, md: 1}} gap='20px'>
                                {rentalTypes.map((type) => (
                                    <Box
                                        cursor="pointer"
                                        onClick={() => handleSelectType(type.id)}
                                        p={5}
                                        borderColor={selectedTypes?.includes(type.id) ? 'brand.500' : 'gray.300'}
                                        borderWidth='1px'
                                        borderRadius='lg'
                                        key={type.id}
                                        value={type.id}>
                                        {type.name}
                                    </Box>
                                ))}
                            </SimpleGrid>
                        </Card>}

                        {shownField === 'amenities' && <Card boxShadow='lg' p={"5"} mb={"2"}>
                            <Heading mb={5}>Ausstattungsmerkmale</Heading>
                            <Text fontSize={'md'} color='grey' mb={5}>Diese Merkmale hast du für deine Unterkunft
                                ausgewählt</Text>
                            <SimpleGrid columns={{base: 1, md: 1}} gap='20px'>
                                {amenityTypes.map((amenity) => (
                                    <Box cursor="pointer"
                                         onClick={() => handleSelectAmenity(amenity.id)}
                                         p={5}
                                         borderColor={selectedAmenities?.includes(amenity.id) ? 'brand.500' : 'gray.300'}
                                         borderWidth='1px'
                                         borderRadius='lg'
                                         key={amenity.id}
                                    >
                                        {amenity.name}
                                    </Box>
                                ))}
                            </SimpleGrid>
                        </Card>}

                        {shownField === 'address' && <Card boxShadow='lg' p={"5"} mb={"2"}>
                            <Heading mb={5}>Adresse</Heading>
                            <Text fontSize={'md'} color='grey' mb={5}>Für deine Unterkunft hast du folgende Adresse
                                angegeben</Text>
                            <MapboxMoveable address={formattedAddress} coordinates={coordinates}
                                            onCoordinatesChange={handleCoordinatesChange}/>
                            <Button variant={'brand'} mt={5} onClick={onOpen}>Anpassen</Button>
                        </Card>}

                        {shownField === 'general' && <Card boxShadow='lg' p={"5"} mb={"2"}>
                            <Heading mb={5}>Allgemeine Angaben</Heading>
                            <Text fontSize={'md'} color='grey' mb={5}>Diese Angaben hast du für deine Unterkunft
                                eingestellt</Text>
                            <FormControl mb={5}>
                                <FormLabel
                                    display='flex'
                                    fontSize='sm'
                                    fontWeight='500'
                                    htmlFor='price'
                                >
                                    Preis pro Übernachtung ab
                                </FormLabel>
                                <InputGroup>
                                    <InputLeftAddon height='auto'>€</InputLeftAddon>
                                    <Controller
                                        name="price"
                                        control={control}
                                        render={({field}) => (
                                            <NumberInput
                                                size='lg'
                                                maxW={32}
                                                defaultValue={10}
                                                min={1}
                                                value={field.value}
                                                onChange={(valueAsString, valueAsNumber) => {
                                                    setValue('price', valueAsNumber);
                                                }}
                                            >
                                                <NumberInputField
                                                    id='price'
                                                    fontSize='sm'
                                                    type='number'
                                                    fontWeight='500'
                                                    size='lg'
                                                    borderLeftRadius={0}
                                                />
                                                <NumberInputStepper>
                                                    <NumberIncrementStepper/>
                                                    <NumberDecrementStepper/>
                                                </NumberInputStepper>
                                            </NumberInput>
                                        )}
                                    />
                                </InputGroup>
                                <FormErrorMessage>
                                    {errors.price && errors.price.message}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl mb={5}>
                                <FormLabel
                                    display='flex'
                                    fontSize='sm'
                                    fontWeight='500'
                                    htmlFor='bathrooms'
                                >
                                    Anzahl Badezimmer
                                </FormLabel>
                                <InputGroup>
                                    <InputLeftAddon height='auto'>
                                        <Icon
                                            transition='0.2s linear'
                                            w='20px'
                                            h='20px'
                                            as={FaBath}
                                        />
                                    </InputLeftAddon>
                                    <Controller
                                        name="bathrooms"
                                        control={control}
                                        render={({field}) => (
                                            <NumberInput
                                                size='lg'
                                                maxW={32}
                                                defaultValue={1}
                                                min={1}
                                                value={field.value}
                                                onChange={(valueAsString, valueAsNumber) => {
                                                    setValue('bathrooms', valueAsNumber);
                                                }}
                                            >
                                                <NumberInputField
                                                    id='bathrooms'
                                                    fontSize='sm'
                                                    type='number'
                                                    fontWeight='500'
                                                    size='lg'
                                                    borderLeftRadius={0}
                                                />
                                                <NumberInputStepper>
                                                    <NumberIncrementStepper/>
                                                    <NumberDecrementStepper/>
                                                </NumberInputStepper>
                                            </NumberInput>
                                        )}
                                    />
                                </InputGroup>
                                <FormErrorMessage>
                                    {errors.bedrooms && errors.bedrooms.message}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl mb={5}>
                                <FormLabel
                                    display='flex'
                                    fontSize='sm'
                                    fontWeight='500'
                                    htmlFor='bedrooms'
                                >
                                    Anzahl Schlafzimmer
                                </FormLabel>
                                <InputGroup>
                                    <InputLeftAddon height='auto'>
                                        <Icon
                                            transition='0.2s linear'
                                            w='20px'
                                            h='20px'
                                            as={IoBed}
                                        />
                                    </InputLeftAddon>
                                    <Controller
                                        name="bedrooms"
                                        control={control}
                                        render={({field}) => (
                                            <NumberInput
                                                size='lg'
                                                maxW={32}
                                                defaultValue={1}
                                                min={1}
                                                value={field.value}
                                                onChange={(valueAsString, valueAsNumber) => {
                                                    setValue('bedrooms', valueAsNumber);
                                                }}
                                            >
                                                <NumberInputField
                                                    id='bedrooms'
                                                    fontSize='sm'
                                                    type='number'
                                                    fontWeight='500'
                                                    size='lg'
                                                    borderLeftRadius={0}
                                                />
                                                <NumberInputStepper>
                                                    <NumberIncrementStepper/>
                                                    <NumberDecrementStepper/>
                                                </NumberInputStepper>
                                            </NumberInput>
                                        )}
                                    />
                                </InputGroup>
                                <FormErrorMessage>
                                    {errors.bedrooms && errors.bedrooms.message}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl mb={5}>
                                <FormLabel
                                    display='flex'
                                    fontSize='sm'
                                    fontWeight='500'
                                    htmlFor='guests'
                                >
                                    Anzahl der Gäste
                                </FormLabel>
                                <InputGroup>
                                    <InputLeftAddon height='auto'>
                                        <Icon
                                            transition='0.2s linear'
                                            w='20px'
                                            h='20px'
                                            as={IoPeople}
                                        />
                                    </InputLeftAddon>
                                    <Controller
                                        name="guests"
                                        control={control}
                                        render={({field}) => (
                                            <NumberInput
                                                size='lg'
                                                maxW={32}
                                                defaultValue={2}
                                                min={1}
                                                value={field.value}
                                                onChange={(valueAsString, valueAsNumber) => {
                                                    setValue('guests', valueAsNumber);
                                                }}
                                            >
                                                <NumberInputField
                                                    id='guests'
                                                    fontSize='sm'
                                                    type='number'
                                                    fontWeight='500'
                                                    size='lg'
                                                    borderLeftRadius={0}
                                                />
                                                <NumberInputStepper>
                                                    <NumberIncrementStepper/>
                                                    <NumberDecrementStepper/>
                                                </NumberInputStepper>
                                            </NumberInput>
                                        )}
                                    />
                                </InputGroup>
                                <FormErrorMessage>
                                    {errors.guests && errors.guests.message}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl mb={5}>
                                <FormLabel
                                    display='flex'
                                    fontSize='sm'
                                    fontWeight='500'
                                    htmlFor='size'
                                >
                                    Größe
                                </FormLabel>
                                <InputGroup>
                                    <InputLeftAddon height='auto'>qm</InputLeftAddon>
                                    <Controller
                                        name="size"
                                        control={control}
                                        render={({field}) => (
                                            <NumberInput
                                                size='lg'
                                                maxW={32}
                                                defaultValue={1}
                                                min={1}
                                                value={field.value}
                                                onChange={(valueAsString, valueAsNumber) => {
                                                    setValue('size', valueAsNumber);
                                                }}
                                            >
                                                <NumberInputField
                                                    id='size'
                                                    fontSize='sm'
                                                    type='number'
                                                    fontWeight='500'
                                                    size='lg'
                                                    borderLeftRadius={0}
                                                />
                                                <NumberInputStepper>
                                                    <NumberIncrementStepper/>
                                                    <NumberDecrementStepper/>
                                                </NumberInputStepper>
                                            </NumberInput>
                                        )}
                                    />
                                </InputGroup>
                                <FormErrorMessage>
                                    {errors.bedrooms && errors.bedrooms.message}
                                </FormErrorMessage>
                            </FormControl>
                        </Card>}

                        {shownField === 'additional_information' && <Card boxShadow='lg' p={"5"} mb={"2"}>
                            <Heading mb={5}>Wichtige Informationen</Heading>
                            <Text fontSize={'md'} color='grey' mb={5}>Diese Informationen hast du angegeben (z.B.
                                Mindestaufenhalt, Stornobedingungen, Endreinigung-Gebühr etc.)</Text>
                            <FormControl>
                                <Controller
                                    name="additional_information"
                                    control={control}
                                    defaultValue=""
                                    render={({field}) => (<MyCKEditor field={field}/>)}
                                />
                            </FormControl>
                        </Card>}

                        {shownField === 'ratings' && <Card boxShadow='lg' p={"5"} mb={"2"}>
                            <Heading mb={5}>Bewertungen</Heading>
                            <Text fontSize={'md'} color='grey' mb={5}>Diese Bewertungen werden für dein Inserat
                                ausgegeben:</Text>
                            <FormControl display="flex" alignItems="center">
                                <FormLabel htmlFor="show_google_reviews" mb="0">
                                    Google:
                                </FormLabel>
                                <Text ml={2}>{showGoogleReviews ? 'Aktiviert' : 'Deaktiviert'}</Text>
                                <Switch
                                    id="show_google_reviews"
                                    isChecked={showGoogleReviews}
                                    onChange={toggleShowGoogleReviews}
                                    ml={'auto'}
                                />
                            </FormControl>
                            {showGoogleReviews ?
                                watch('google_place_data') ?
                                    <Box mt={3}>
                                        <Text fontSize="md" fontWeight="semibold">{watch('google_place_data').name}</Text>
                                        <Text fontSize="sm" color="gray.600">{watch('google_place_data').formatted_address}</Text>
                                        <Flex align="center" mt={1}>
                                            <RatingStars rating={watch('google_place_data')?.rating}/>
                                            <Text fontSize="sm" color="gray.500" ml={2}>
                                                ({watch('google_place_data')?.user_ratings_total > 0 ? watch('google_place_data')?.user_ratings_total : 'keine'} Bewertungen)
                                            </Text>
                                        </Flex>
                                        <Button variant={'brand'} mt={5} onClick={onOpenRating}>Anpassen</Button>
                                    </Box>
                                    : <Box mt={3}>
                                        <Text fontSize="md" fontWeight="semibold">Es wurden keine Bewertungen gefunden.</Text>
                                        <Button variant={'brand'} mt={5} onClick={onOpenRating}>Anpassen</Button>
                                    </Box>
                                : ''
                            }
                        </Card>}

                        {shownField === 'optional' && <Card boxShadow='lg' p={"5"} mb={"2"}>
                            <Heading mb={5}>Optionale Angaben</Heading>
                            <Text fontSize={'md'} color='grey' mb={5}>Du hast folgende Angaben für dein Inserat
                                hinterlegt</Text>
                            <FormControl mb={5}>
                                <FormLabel
                                    display='flex'
                                    fontSize='lg'
                                    fontWeight='500'
                                    htmlFor='link'
                                >
                                    Webseite
                                </FormLabel>
                                <Input
                                    id='link'
                                    fontSize='sm'
                                    type='text'
                                    fontWeight='500'
                                    placeholder={"Link zur Webseite"}
                                    size='lg'
                                    {...register('link', {
                                        onChange: handleUrlChange('link'),
                                        pattern: {
                                            value: /^(ftp|http|https):\/\/[^ "]+$/,
                                            message: 'Bitte geben Sie eine gültige URL an'
                                        }
                                    })}
                                />
                                <FormErrorMessage>
                                    {errors.link && errors.link.message}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl mb={5}>
                                <FormLabel
                                    display='flex'
                                    fontSize='lg'
                                    fontWeight='500'
                                    htmlFor='facebook'
                                >
                                    Facebook
                                </FormLabel>
                                <Input
                                    id='facebook'
                                    fontSize='sm'
                                    type='text'
                                    fontWeight='500'
                                    size='lg'
                                    placeholder={"Link zu Facebook"}
                                    {...register('facebook', {
                                        onChange: handleUrlChange('facebook'),
                                        pattern: {
                                            value: /^(ftp|http|https):\/\/[^ "]+$/,
                                            message: 'Bitte geben Sie eine gültige URL an'
                                        }
                                    })}
                                />
                                <FormErrorMessage>
                                    {errors.facebook && errors.facebook.message}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl>
                                <FormLabel
                                    display='flex'
                                    fontSize='lg'
                                    fontWeight='500'
                                    htmlFor='instagram'
                                >
                                    Instagram
                                </FormLabel>
                                <Input
                                    id='instagram'
                                    fontSize='sm'
                                    type='text'
                                    fontWeight='500'
                                    size='lg'
                                    placeholder={"Link zu Instagram"}
                                    {...register('instagram', {
                                        onChange: handleUrlChange('instagram'),
                                        pattern: {
                                            value: /^(ftp|http|https):\/\/[^ "]+$/,
                                            message: 'Bitte geben Sie eine gültige URL an'
                                        }
                                    })}
                                />
                                <FormErrorMessage>
                                    {errors.instagram && errors.instagram.message}
                                </FormErrorMessage>
                            </FormControl>
                        </Card>}
                    </Box>
                </Flex>
                <Flex gap='15px'>
                    {/*<Button*/}
                    {/*    fontSize='sm'*/}
                    {/*    variant='outline'*/}
                    {/*    fontWeight='500'*/}
                    {/*    w='100%'*/}
                    {/*    h='50'*/}
                    {/*    mt='24px'*/}
                    {/*    mb='24px'*/}
                    {/*    type='submit'*/}
                    {/*>*/}
                    {/*    <Icon*/}
                    {/*        transition='0.2s linear'*/}
                    {/*        w='20px'*/}
                    {/*        h='20px'*/}
                    {/*        as={IoEyeOutline}*/}
                    {/*        color='brand.500'*/}
                    {/*        mx='15px'*/}
                    {/*    /> Anzeigen*/}
                    {/*</Button>*/}
                    <Button
                        fontSize='sm'
                        variant='brand'
                        fontWeight='500'
                        w='100%'
                        h='50'
                        mt='24px'
                        mb='24px'
                        isLoading={isSubmitting}
                        type='submit'
                    >
                        Änderungen speichern
                    </Button>
                </Flex>
            </form>
            <Modal isOpen={isOpenRating} onClose={onCloseRating}>
                <ModalOverlay/>
                <ModalContent bgColor="white">
                    <ModalHeader>Google Bewertung anpassen</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody>
                        <Text mb={3}>Suche hier nach deinem Objekt und wähle den passenden Eintrag:</Text>
                        <PlaceSearch onPlaceSelected={handleGooglePlaceSelected}/>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant='ghost' mr={3} onClick={onCloseRating}>
                            Schließen
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay/>
                <ModalContent bgColor="white">
                    <ModalHeader>Adresse anpassen</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody>
                        {address ?
                            <Box>
                                <FormControl mb='5' isInvalid={errors.temp_country} display={'none'}>
                                    <FormLabel
                                        display='flex'
                                        ms='4px'
                                        fontSize='sm'
                                        fontWeight='500'
                                        htmlFor='temp_country'
                                    >
                                        Land
                                    </FormLabel>
                                    <Input
                                        id='temp_country'
                                        fontSize='sm'
                                        type='text'
                                        fontWeight='500'
                                        size='lg'
                                        {...register('temp_country', {
                                            required: 'Bitte gib das Land der Unterkunft an',
                                        })}
                                    />
                                    <FormErrorMessage>
                                        {errors.temp_country && errors.temp_country.message}
                                    </FormErrorMessage>
                                </FormControl>
                                <FormControl mb='5' isInvalid={errors.temp_street}>
                                    <FormLabel
                                        display='flex'
                                        ms='4px'
                                        fontSize='sm'
                                        fontWeight='500'
                                        htmlFor='temp_street'
                                    >
                                        Strasse und Hausnummer<Text
                                        color={brandStars}>*</Text>
                                    </FormLabel>
                                    <Input
                                        id='temp_street'
                                        fontSize='sm'
                                        type='text'
                                        fontWeight='500'
                                        size='lg'
                                        {...register('temp_street', {
                                            required: 'Bitte gib die Strasse und Hausnummer der Unterkunft an',
                                        })}
                                    />
                                    <FormErrorMessage>
                                        {errors.temp_street && errors.temp_street.message}
                                    </FormErrorMessage>
                                </FormControl>
                                <FormControl mb='5'>
                                    <FormLabel
                                        display='flex'
                                        ms='4px'
                                        fontSize='sm'
                                        fontWeight='500'
                                        htmlFor='temp_zip'
                                    >
                                        PLZ
                                    </FormLabel>
                                    <Input
                                        id='temp_zip'
                                        fontSize='sm'
                                        type='text'
                                        fontWeight='500'
                                        size='lg'
                                        {...register('temp_zip')}
                                    />
                                </FormControl>
                                <FormControl mb='5' isInvalid={errors.temp_place}>
                                    <FormLabel
                                        display='flex'
                                        ms='4px'
                                        fontSize='sm'
                                        fontWeight='500'
                                        htmlFor='temp_place'
                                    >
                                        Stadt/Ort<Text
                                        color={brandStars}>*</Text>
                                    </FormLabel>
                                    <Input
                                        id='temp_place'
                                        fontSize='sm'
                                        type='text'
                                        fontWeight='500'
                                        size='lg'
                                        {...register('temp_place', {
                                            required: 'Bitte gib die Stadt / den Ort der Unterkunft an',
                                        })}
                                    />
                                    <FormErrorMessage>
                                        {errors.temp_place && errors.temp_place.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </Box> : ''}
                        <Mapbox onAddressChange={handleAddressChange}></Mapbox>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant='ghost' mr={3} onClick={onClose}>
                            Schließen
                        </Button>
                        <Button variant='brand' onClick={handleUpdateAddress}
                                isDisabled={!watch('temp_street') || !watch('temp_place')}>Übernehmen</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
